import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerService } from '../../services/customerService';
import productService, { Product } from '../../services/productService';
import stockMovementService, { SerialNumber } from '../../services/stockMovementService';
import { invoiceTemplateService } from '../../services/invoiceTemplateService';
import { Invoice, InvoiceFormData, InvoiceItemFormData, CreateInvoiceData, RecurrencePattern } from '../../types/invoice';
import { Customer } from '../../types/customer';
import { formatCurrency } from '../../utils/formatters';

interface InvoiceFormProps {
  invoice?: Invoice;
  initialCustomerId?: string;
  initialJobId?: string;
  onSubmit: (data: CreateInvoiceData) => void;
  onCancel: () => void;
}

// ── Design tokens ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box'
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  border: '1px solid rgba(201,90,90,0.4)'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6
};

const sectionStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
  padding: 20
};

const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#d97706',
  marginBottom: 16,
  paddingBottom: 10,
  borderBottom: '1px solid rgba(92,78,51,0.15)'
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '9px 20px',
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontWeight: 600
};

const ghostBtnStyle: React.CSSProperties = {
  padding: '9px 20px',
  background: 'transparent',
  color: '#6b6a5c',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};

const dangerBtnStyle: React.CSSProperties = {
  padding: '7px 14px',
  background: 'transparent',
  color: '#c95a5a',
  border: '1px solid rgba(201,90,90,0.3)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};

const smallInputStyle: React.CSSProperties = {
  padding: '7px 10px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box',
  width: '100%'
};

const errorMsgStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 11,
  color: '#c95a5a',
  marginTop: 4,
  margin: '4px 0 0 0'
};

// ── Component ────────────────────────────────────────────────────────────────

export const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoice,
  initialCustomerId,
  initialJobId,
  onSubmit,
  onCancel
}) => {
  const isEditing = !!invoice;

  const [formData, setFormData] = useState<InvoiceFormData>({
    customerId: invoice?.customerId || initialCustomerId || '',
    templateId: invoice?.templateId || invoice?.template?.id || '',
    issueDate: invoice?.issueDate
      ? new Date(invoice.issueDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    dueDate: invoice?.dueDate
      ? new Date(invoice.dueDate).toISOString().split('T')[0]
      : '',
    notes: invoice?.notes || '',
    isRecurring: invoice?.isRecurring || false,
    recurrencePattern: invoice?.recurrencePattern,
    recurrenceEndDate: invoice?.recurrenceEndDate
      ? new Date(invoice.recurrenceEndDate).toISOString().split('T')[0]
      : '',
    items: invoice?.items?.map(item => ({
      productId: (item as any).productId || '',
      description: item.description,
      quantity: item.quantity.toString(),
      rate: item.rate.toString(),
      taxRate: item.taxRate?.toString() || '0',
      amount: item.amount,
      taxAmount: item.taxAmount || 0,
      serialNumbers: item.serialNumbers || []
    })) || []
  });

  // Available serial numbers keyed by productId
  const [availableSerialsByProduct, setAvailableSerialsByProduct] = useState<
    Record<string, SerialNumber[]>
  >({});

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: customersData } = useQuery({
    queryKey: ['customers', { limit: 100 }],
    queryFn: () => customerService.getCustomers({ limit: 100 })
  });

  const { data: productsData } = useQuery({
    queryKey: ['products', { limit: 100, isActive: true }],
    queryFn: () => productService.getProducts({ limit: 100, isActive: true })
  });

  const { data: templatesData } = useQuery({
    queryKey: ['invoiceTemplates'],
    queryFn: () => invoiceTemplateService.getAll(false)
  });

  const customers = customersData?.data || [];
  const products: Product[] = productsData?.data || [];
  const templates = templatesData || [];

  // ── Line-item handlers ───────────────────────────────────────────────────

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: '',
          description: '',
          quantity: '1',
          rate: '0',
          taxRate: '0',
          amount: 0,
          taxAmount: 0,
          serialNumbers: []
        }
      ]
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (
    index: number,
    field: keyof InvoiceItemFormData,
    value: string | number | string[]
  ) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      // Auto-populate product details when product is selected
      if (field === 'productId' && typeof value === 'string') {
        const selectedProduct = products.find(p => p.id === value);
        if (selectedProduct) {
          newItems[index].description = selectedProduct.name;
          newItems[index].rate = selectedProduct.unitPrice?.toString() || '0';
          newItems[index].serialNumbers = [];

          if (selectedProduct.isSerialized) {
            stockMovementService
              .getAvailableSerialNumbers(value)
              .then(serials => {
                setAvailableSerialsByProduct(prev => ({ ...prev, [value]: serials }));
              })
              .catch(err => {
                console.error('Error fetching serial numbers:', err);
              });
          }
        }
      }

      // Recalculate amounts
      if (
        field === 'quantity' ||
        field === 'rate' ||
        field === 'taxRate' ||
        field === 'productId'
      ) {
        const qty = parseFloat(newItems[index].quantity) || 0;
        const rate = parseFloat(newItems[index].rate) || 0;
        const tax = parseFloat(newItems[index].taxRate) || 0;
        const amount = qty * rate;
        const taxAmount = (amount * tax) / 100;
        newItems[index].amount = amount;
        newItems[index].taxAmount = taxAmount;
      }

      return { ...prev, items: newItems };
    });
  };

  // ── Totals ───────────────────────────────────────────────────────────────

  const calculateTotals = useCallback(() => {
    const result = formData.items.reduce(
      (acc, item) => ({
        subtotal: acc.subtotal + item.amount,
        taxAmount: acc.taxAmount + item.taxAmount,
        total: 0
      }),
      { subtotal: 0, taxAmount: 0, total: 0 }
    );
    result.total = result.subtotal + result.taxAmount;
    return result;
  }, [formData.items]);

  const totals = useMemo(() => calculateTotals(), [calculateTotals]);

  // ── Validation ───────────────────────────────────────────────────────────

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.customerId) newErrors.customerId = 'Customer is required';
    if (!formData.templateId) newErrors.templateId = 'Invoice template is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';

    if (formData.items.length === 0 && !initialJobId) {
      newErrors.items = 'At least one item is required';
    }

    formData.items.forEach((item, index) => {
      if (!item.productId)
        newErrors[`items.${index}.productId`] = 'Product is required';
      if (!item.description.trim())
        newErrors[`items.${index}.description`] = 'Description is required';
      if (parseFloat(item.quantity) <= 0)
        newErrors[`items.${index}.quantity`] = 'Quantity must be greater than 0';
      if (parseFloat(item.rate) <= 0)
        newErrors[`items.${index}.rate`] = 'Rate must be greater than 0';

      const selectedProduct = products.find(p => p.id === item.productId);
      if (selectedProduct?.isSerialized) {
        const quantity = parseFloat(item.quantity);
        const selectedSerialCount = item.serialNumbers?.length || 0;
        if (quantity !== selectedSerialCount) {
          newErrors[`items.${index}.serialNumbers`] =
            `You must select exactly ${quantity} serial number(s)`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData: CreateInvoiceData = {
      customerId: formData.customerId,
      ...(initialJobId && { jobId: initialJobId }),
      templateId: formData.templateId,
      issueDate: formData.issueDate,
      dueDate: formData.dueDate,
      notes: formData.notes || undefined,
      isRecurring: formData.isRecurring,
      recurrencePattern: formData.recurrencePattern,
      recurrenceEndDate: formData.recurrenceEndDate || undefined,
      items: formData.items.map(item => ({
        productId: item.productId,
        description: item.description,
        quantity: parseFloat(item.quantity),
        rate: parseFloat(item.rate),
        taxRate: parseFloat(item.taxRate) || undefined,
        serialNumbers:
          item.serialNumbers && item.serialNumbers.length > 0
            ? item.serialNumbers
            : undefined
      }))
    };

    console.log('Frontend submitting invoice data:', JSON.stringify(submitData, null, 2));
    onSubmit(submitData);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div style={{
        padding: '0 0 20px 0',
        borderBottom: '1px solid rgba(92,78,51,0.15)',
        marginBottom: 24
      }}>
        <div style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 9,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: '#6b6a5c',
          marginBottom: 6
        }}>
          Billing
        </div>
        <h2 style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 24,
          fontWeight: 300,
          color: '#1c2033',
          margin: 0,
          letterSpacing: '0.04em'
        }}>
          {isEditing ? 'Edit Invoice' : 'Create New Invoice'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Invoice Details ──────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Invoice Details</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>

            {/* Customer */}
            <div>
              <label style={labelStyle}>
                Customer <span style={{ color: '#c95a5a' }}>*</span>
              </label>
              <select
                value={formData.customerId}
                onChange={e => setFormData(prev => ({ ...prev, customerId: e.target.value }))}
                style={errors.customerId ? inputErrorStyle : inputStyle}
              >
                <option value="">Select a customer</option>
                {customers.map((customer: Customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
              {errors.customerId && (
                <p style={errorMsgStyle}>{errors.customerId}</p>
              )}
            </div>

            {/* Template */}
            <div>
              <label style={labelStyle}>
                Invoice Template <span style={{ color: '#c95a5a' }}>*</span>
              </label>
              <select
                value={formData.templateId}
                onChange={e => setFormData(prev => ({ ...prev, templateId: e.target.value }))}
                style={errors.templateId ? inputErrorStyle : inputStyle}
              >
                <option value="">Select a template</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} - {template.templateCompanyName}
                  </option>
                ))}
              </select>
              {errors.templateId && (
                <p style={errorMsgStyle}>{errors.templateId}</p>
              )}
            </div>

            {/* Issue Date */}
            <div>
              <label style={labelStyle}>Issue Date</label>
              <input
                type="date"
                value={formData.issueDate}
                onChange={e => setFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                style={inputStyle}
              />
            </div>

            {/* Due Date */}
            <div>
              <label style={labelStyle}>
                Due Date <span style={{ color: '#c95a5a' }}>*</span>
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={e => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                style={errors.dueDate ? inputErrorStyle : inputStyle}
              />
              {errors.dueDate && (
                <p style={errorMsgStyle}>{errors.dueDate}</p>
              )}
            </div>
          </div>
        </div>

        {/* ── Line Items ───────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            paddingBottom: 10,
            borderBottom: '1px solid rgba(92,78,51,0.15)'
          }}>
            <div style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 9,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#d97706'
            }}>
              Line Items
            </div>
            <button
              type="button"
              onClick={addItem}
              style={{ ...primaryBtnStyle, padding: '6px 14px', fontSize: 9 }}
            >
              + Add Item
            </button>
          </div>

          {errors.items && (
            <div style={{
              background: 'rgba(201,90,90,0.08)',
              border: '1px solid rgba(201,90,90,0.2)',
              borderRadius: 4,
              padding: '10px 14px',
              marginBottom: 14
            }}>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#c95a5a',
                margin: 0
              }}>
                {errors.items}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {formData.items.map((item, index) => (
              <div
                key={index}
                style={{
                  background: '#f4efe2',
                  borderBottom: '1px solid rgba(239,232,213,0.6)',
                  borderRadius: 6,
                  padding: '8px 0',
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingTop: 16,
                  paddingBottom: 16
                }}
              >
                {/* Top row: Product + Remove */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 12,
                  marginBottom: 12
                }}>
                  <div>
                    <label style={labelStyle}>
                      Product <span style={{ color: '#c95a5a' }}>*</span>
                    </label>
                    <select
                      value={item.productId}
                      onChange={e => updateItem(index, 'productId', e.target.value)}
                      style={errors[`items.${index}.productId`] ? inputErrorStyle : inputStyle}
                    >
                      <option value="">Select a product</option>
                      {products.map((p: Product) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.sku})
                        </option>
                      ))}
                    </select>
                    {errors[`items.${index}.productId`] && (
                      <p style={errorMsgStyle}>{errors[`items.${index}.productId`]}</p>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      style={dangerBtnStyle}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* Qty / Rate / Tax row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 12,
                  marginBottom: 12
                }}>
                  <div>
                    <label style={labelStyle}>
                      Quantity <span style={{ color: '#c95a5a' }}>*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quantity}
                      onChange={e => updateItem(index, 'quantity', e.target.value)}
                      style={errors[`items.${index}.quantity`] ? inputErrorStyle : smallInputStyle}
                    />
                    {errors[`items.${index}.quantity`] && (
                      <p style={errorMsgStyle}>{errors[`items.${index}.quantity`]}</p>
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>
                      Rate <span style={{ color: '#c95a5a' }}>*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.rate}
                      onChange={e => updateItem(index, 'rate', e.target.value)}
                      style={errors[`items.${index}.rate`] ? inputErrorStyle : smallInputStyle}
                    />
                    {errors[`items.${index}.rate`] && (
                      <p style={errorMsgStyle}>{errors[`items.${index}.rate`]}</p>
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>Tax Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.taxRate}
                      onChange={e => updateItem(index, 'taxRate', e.target.value)}
                      style={smallInputStyle}
                    />
                  </div>
                </div>

                {/* Description */}
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>
                    Description <span style={{ color: '#c95a5a' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={e => updateItem(index, 'description', e.target.value)}
                    placeholder="Enter item description"
                    style={errors[`items.${index}.description`] ? inputErrorStyle : inputStyle}
                  />
                  {errors[`items.${index}.description`] && (
                    <p style={errorMsgStyle}>{errors[`items.${index}.description`]}</p>
                  )}
                </div>

                {/* Serial numbers (serialized products only) */}
                {(() => {
                  const selectedProduct = products.find(p => p.id === item.productId);
                  if (!selectedProduct?.isSerialized) return null;

                  const availableSerials = availableSerialsByProduct[item.productId || ''] || [];
                  const selectedSerials = item.serialNumbers || [];

                  return (
                    <div style={{ marginBottom: 12 }}>
                      <label style={labelStyle}>
                        Serial Numbers <span style={{ color: '#c95a5a' }}>*</span>
                        <span style={{
                          marginLeft: 8,
                          color: '#5b5a52',
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 10,
                          textTransform: 'none',
                          letterSpacing: 0
                        }}>
                          (Select {item.quantity || 0})
                        </span>
                      </label>
                      <div style={{
                        border: '1px solid rgba(92,78,51,0.25)',
                        borderRadius: 4,
                        padding: 8,
                        maxHeight: 140,
                        overflowY: 'auto',
                        background: '#f4efe2'
                      }}>
                        {availableSerials.length === 0 ? (
                          <p style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: 12,
                            color: '#6b6a5c',
                            fontStyle: 'italic',
                            margin: 0
                          }}>
                            No available serial numbers for this product
                          </p>
                        ) : (
                          availableSerials.map(serial => (
                            <label
                              key={serial.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '5px 8px',
                                cursor: 'pointer',
                                borderRadius: 3
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedSerials.includes(serial.serialNumber)}
                                onChange={e => {
                                  const newSerials = e.target.checked
                                    ? [...selectedSerials, serial.serialNumber]
                                    : selectedSerials.filter(s => s !== serial.serialNumber);
                                  updateItem(index, 'serialNumbers', newSerials);
                                }}
                                style={{ accentColor: '#d97706', width: 12, height: 12 }}
                              />
                              <span style={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: 12,
                                color: '#5b5a52'
                              }}>
                                {serial.serialNumber}
                                {serial.warehouse && (
                                  <span style={{ marginLeft: 8, color: '#6b6a5c', fontSize: 11 }}>
                                    ({serial.warehouse.name})
                                  </span>
                                )}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                      {errors[`items.${index}.serialNumbers`] && (
                        <p style={errorMsgStyle}>{errors[`items.${index}.serialNumbers`]}</p>
                      )}
                      {selectedSerials.length > 0 && (
                        <p style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 11,
                          color: '#6b6a5c',
                          marginTop: 4,
                          margin: '4px 0 0 0'
                        }}>
                          Selected: {selectedSerials.length} of {item.quantity || 0}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Line summary */}
                <div style={{
                  textAlign: 'right',
                  borderTop: '1px solid rgba(239,232,213,0.6)',
                  paddingTop: 10
                }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
                    Amount:{' '}
                    <span style={{ color: '#5b5a52' }}>{formatCurrency(item.amount)}</span>
                    &nbsp;&nbsp;Tax:{' '}
                    <span style={{ color: '#5b5a52' }}>{formatCurrency(item.taxAmount)}</span>
                    &nbsp;&nbsp;
                  </span>
                  <span style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 15,
                    color: '#d97706'
                  }}>
                    Total: {formatCurrency(item.amount + item.taxAmount)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Notes ───────────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Notes</div>
          <textarea
            rows={3}
            value={formData.notes}
            onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add any additional notes..."
            style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
          />
        </div>

        {/* ── Recurring Invoice ───────────────────────────────────────── */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Recurring Invoice</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: formData.isRecurring ? 16 : 0 }}>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, isRecurring: !prev.isRecurring, recurrencePattern: undefined, recurrenceEndDate: '' }))}
              style={{
                width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                background: formData.isRecurring ? '#d97706' : 'rgba(92,78,51,0.35)',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: formData.isRecurring ? 21 : 3,
                width: 16, height: 16, borderRadius: '50%', background: '#ffffff',
                transition: 'left 0.2s', display: 'block',
              }} />
            </button>
            <div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: '#1c2033' }}>
                Recurring Invoice
              </span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginLeft: 8 }}>
                Automatically generate a new invoice on a schedule
              </span>
            </div>
          </div>

          {formData.isRecurring && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
              <div>
                <label style={labelStyle}>Recurrence Pattern <span style={{ color: '#c95a5a' }}>*</span></label>
                <select
                  value={formData.recurrencePattern || ''}
                  onChange={e => setFormData(prev => ({ ...prev, recurrencePattern: (e.target.value as RecurrencePattern) || undefined }))}
                  style={{ ...inputStyle, appearance: 'none' }}
                  required={formData.isRecurring}
                >
                  <option value="">Select frequency</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly (every 3 months)</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>End Date <span style={{ color: '#8a8778' }}>(Optional)</span></label>
                <input
                  type="date"
                  value={formData.recurrenceEndDate || ''}
                  onChange={e => setFormData(prev => ({ ...prev, recurrenceEndDate: e.target.value }))}
                  style={{ ...inputStyle, colorScheme: 'light' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{
                  background: 'rgba(217,119,6,0.07)', border: '1px solid rgba(217,119,6,0.18)',
                  borderRadius: 4, padding: '8px 12px',
                  fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#92400e',
                }}>
                  📅 A new <strong>DRAFT</strong> invoice with the same items will be generated automatically when this invoice's period ends.
                  {formData.recurrencePattern && (
                    <span> Next invoice will be due on the same day of the {
                      { WEEKLY: 'next week', MONTHLY: 'next month', QUARTERLY: 'next quarter', YEARLY: 'next year' }[formData.recurrencePattern]
                    }.</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Totals panel ────────────────────────────────────────────── */}
        {formData.items.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{
              width: 300,
              background: '#ffffff',
              border: '1px solid rgba(92,78,51,0.15)',
              borderRadius: 6,
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10
            }}>
              {([
                ['Subtotal', formatCurrency(totals.subtotal)],
                ['Tax', formatCurrency(totals.taxAmount)]
              ] as [string, string][]).map(([label, value]) => (
                <div
                  key={label}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: '#6b6a5c'
                  }}>
                    {label}
                  </span>
                  <span style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13,
                    color: '#5b5a52'
                  }}>
                    {value}
                  </span>
                </div>
              ))}

              {/* Grand total row */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 10,
                borderTop: '1px solid rgba(92,78,51,0.25)',
                marginTop: 2
              }}>
                <span style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 10,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#d97706'
                }}>
                  Total
                </span>
                <span style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 24,
                  fontWeight: 300,
                  color: '#d97706'
                }}>
                  {formatCurrency(totals.total)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Form actions ─────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          paddingTop: 8,
          borderTop: '1px solid rgba(92,78,51,0.15)'
        }}>
          <button type="button" onClick={onCancel} style={ghostBtnStyle}>
            Cancel
          </button>
          <button type="submit" style={primaryBtnStyle}>
            {isEditing ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>
  );
};
